import { Text } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { HIGHLIGHTS_CARD_NAME } from '../lib/constants'
import { WhatsNextCard } from './WhatsNextCard'
import {
  SortedAutomaticallyCard,
  toSortedAutomaticallyContent,
} from './SortedAutomaticallyCard'

interface Highlight {
  value: string
  label: string
}

interface HighlightsCardContent {
  name: unknown
  title: string
  subtitle: string
  highlights: Highlight[]
}

interface HighlightsCardProps {
  result: unknown
  onAsk: (prompt: string) => void
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isHighlight = (value: unknown): value is Highlight =>
  isRecord(value) &&
  typeof value.value === 'string' &&
  typeof value.label === 'string'

const parseResult = (result: unknown): unknown => {
  if (typeof result !== 'string') return result
  try {
    return JSON.parse(result)
  } catch {
    return null
  }
}

const toContent = (result: unknown): HighlightsCardContent | null => {
  const parsed = parseResult(result)
  if (!isRecord(parsed) || parsed.shown !== true) return null
  const { card } = parsed
  if (!isRecord(card)) return null
  const { title, subtitle, highlights } = card
  if (typeof title !== 'string' || typeof subtitle !== 'string') return null
  if (!Array.isArray(highlights) || highlights.length === 0) return null
  if (!highlights.every(isHighlight)) return null
  return { name: parsed.name, title, subtitle, highlights }
}

const toSortedAutomatically = (result: unknown) => {
  const parsed = parseResult(result)
  if (!isRecord(parsed) || parsed.shown !== true) return null
  if (parsed.name !== HIGHLIGHTS_CARD_NAME.sortedAutomatically) return null
  return toSortedAutomaticallyContent(parsed.card)
}

export const HighlightsCard = ({ result, onAsk }: HighlightsCardProps) => {
  const sorted = toSortedAutomatically(result)
  if (sorted) return <SortedAutomaticallyCard content={sorted} onAsk={onAsk} />

  const content = toContent(result)
  if (!content) return null

  return (
    <>
      <div className="my-2 flex flex-col gap-4 rounded-[16px] border border-neutral-200 bg-neutral-0 p-4 shadow-sm">
        <div className="flex flex-col gap-1">
          <Text variant={TypographyStyles.LABEL_MD} color={TextColors.HEADER}>
            {content.title}
          </Text>
          <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
            {content.subtitle}
          </Text>
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {content.highlights.map(({ value, label }) => (
            <div
              key={label}
              className="flex flex-col gap-1 rounded-[8px] bg-neutral-25 p-3"
            >
              <Text variant={TypographyStyles.DISPLAY_SM}>{value}</Text>
              <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
                {label}
              </Text>
            </div>
          ))}
        </div>
      </div>
      {content.name === HIGHLIGHTS_CARD_NAME.cubkComplete && <WhatsNextCard />}
    </>
  )
}
